import { useEffect, useState } from 'react';
import type { CompanyProfile, NicheProcessDto } from '@swg/contracts';
import { fetchProfile } from '../api.js';
import { theme } from '../theme.js';

/**
 * De niche (P20/P21) in de weergave (P22).
 *
 * De niche is het scherpste dat we over het eigen bedrijf zeggen — scherper dan
 * `industry` en veel scherper dan de SBI-code, die een hele familie van
 * behandelingen omvat waarvan het bedrijf er meestal maar een paar levert. Ze
 * komt daarom op meerdere tabbladen terug, maar overal met dezelfde twee regels:
 *
 * 1. **Elke procesregel toont zijn bron.** `sourceUrl` is er altijd — een regel
 *    zonder geciteerde bron is bij het ophalen al weggegooid (`groundNiche`).
 *    De bron tonen is dus geen versiering maar de reden dat de regel er staat.
 * 2. **Leeg is niet nul.** Geen procespakket betekent "nog niet vastgesteld",
 *    niet "dit bedrijf levert niets". Dezelfde regel als `NOT_AVAILABLE` bij de
 *    metrieken.
 */

/** Toont het domein van een bron-URL; valt terug op de hele URL. */
export function sourceHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

const cardStyle = {
  background: theme.color.surface,
  border: `1px solid ${theme.color.border}`,
  borderRadius: 10,
  padding: '16px 18px',
} as const;

const labelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: theme.color.muted,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

/**
 * Het procespakket als lijst, met per regel de bron waarop het is teruggevonden.
 *
 * `onRemove` is optioneel: zonder die prop is de lijst alleen-lezen. Verwijderen
 * is een handmatige correctie en gaat via `patchProfile`, waardoor de herkomst
 * van het veld op MANUAL komt te staan en de regel niet stil wordt overschreven.
 */
export function NicheProcessList({
  processes,
  onRemove,
  busy,
}: {
  processes: NicheProcessDto[];
  onRemove?: (index: number) => void;
  busy?: boolean;
}) {
  if (processes.length === 0) {
    return (
      <div style={{ fontSize: 13.5, color: theme.color.muted, lineHeight: 1.5 }}>
        Er is nog geen procespakket vastgesteld. Dat betekent niet dat het bedrijf niets levert —
        alleen dat er geen regel is die we op een eigen bron konden terugvinden.
        {onRemove && ' Vul hieronder zelf aan wat dit bedrijf levert; daarmee gaat het zoeken naar concurrenten alsnog van start.'}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {processes.map((p, i) => (
        <div
          key={`${p.process}-${i}`}
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 12,
            border: `1px solid ${theme.color.border}`,
            borderRadius: 8,
            padding: '9px 12px',
            background: theme.color.bg,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{p.process}</div>
            {p.detail && (
              <div style={{ fontSize: 12.5, color: theme.color.muted, marginTop: 2, lineHeight: 1.45 }}>
                {p.detail}
              </div>
            )}
            <div style={{ fontSize: 11.5, color: theme.color.subtle, marginTop: 4 }}>
              bron:{' '}
              <a href={p.sourceUrl} target="_blank" rel="noreferrer" style={{ color: theme.color.primary }}>
                {sourceHost(p.sourceUrl)}
              </a>
            </div>
          </div>
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(i)}
              disabled={busy}
              title="Deze regel verwijderen — het bedrijf levert dit niet"
              style={{
                background: 'transparent',
                border: 'none',
                color: theme.color.subtle,
                fontSize: 12,
                cursor: busy ? 'default' : 'pointer',
                fontFamily: 'inherit',
                whiteSpace: 'nowrap',
                padding: 0,
              }}
            >
              Verwijderen
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * De vastgestelde niche op tabblad 1 (Invoer).
 *
 * Haalt het profiel zelf op en toont niets zolang er geen niche is: op tabblad 1
 * staat de run vaak nog aan het begin, en een lege kaart zou dan suggereren dat
 * er iets mis is in plaats van dat de stap nog moet draaien.
 */
export function NicheOnInput({ runId }: { runId: string }) {
  const [profile, setProfile] = useState<CompanyProfile | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchProfile(runId)
      .then((p) => !cancelled && setProfile(p))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [runId]);

  if (!profile) return null;
  const { nicheSummary, nicheProcesses } = profile;
  if (!nicheSummary && nicheProcesses.length === 0) return null;

  return (
    <div style={{ ...cardStyle, marginTop: 14 }}>
      <div style={{ ...labelStyle, marginBottom: 7 }}>
        Vastgestelde focus / niche{' '}
        <span style={{ textTransform: 'none', fontWeight: 400, color: theme.color.subtle }}>
          (bepaalt de branchevergelijking en het zoeken naar concurrenten)
        </span>
      </div>
      {nicheSummary && <div style={{ fontSize: 14, lineHeight: 1.5 }}>{nicheSummary}</div>}
      {nicheProcesses.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: nicheSummary ? 10 : 0 }}>
          {nicheProcesses.map((p, i) => (
            <a
              key={`${p.process}-${i}`}
              href={p.sourceUrl}
              target="_blank"
              rel="noreferrer"
              title={`bron: ${sourceHost(p.sourceUrl)}`}
              style={{
                fontSize: 12.5,
                background: theme.color.accentBg,
                color: theme.color.primary,
                borderRadius: 6,
                padding: '3px 9px',
                textDecoration: 'none',
              }}
            >
              {p.process}
            </a>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: theme.color.subtle, marginTop: 10, lineHeight: 1.5 }}>
        Elke regel is teruggevonden op een geciteerde bron; wat niet te onderbouwen was, staat er
        niet. Corrigeren kan bij het bedrijfsprofiel.
      </div>
    </div>
  );
}

/**
 * Een procesregel handmatig toevoegen.
 *
 * Nodig omdat het longlijstkanaal op `nicheProcesses` draait: zonder regels
 * draait het niet, en dan kan een analist die de niche wél kent er niets aan
 * doen. Alleen een samenvatting typen helpt niet — die stuurt het zoeken niet.
 *
 * **De bron-URL is verplicht, ook hier.** Dat is dezelfde eis als bij de
 * AI-regels en om dezelfde reden: een procesregel zonder herleidbare bron is
 * precies wat deze laag moet voorkomen, of hij nu van een model of van een mens
 * komt. Het verschil is dat de analist zelf voor de bron instaat — de regel
 * krijgt herkomst MANUAL.
 */
export function NicheProcessForm({
  onAdd,
  busy,
}: {
  onAdd: (process: NicheProcessDto) => void;
  busy?: boolean;
}) {
  const [process, setProcess] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const inputStyle = {
    padding: '7px 10px',
    fontSize: 13,
    border: `1px solid ${theme.color.borderStrong}`,
    borderRadius: 7,
    fontFamily: 'inherit',
    boxSizing: 'border-box' as const,
  };

  function submit() {
    const proces = process.trim();
    const bron = sourceUrl.trim();
    if (!proces) return setError('Vul in welk proces of welke dienst het bedrijf levert.');
    try {
      const parsed = new URL(bron);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') throw new Error('schema');
    } catch {
      setError('Vul een volledige bron-URL in, inclusief https://');
      return;
    }
    setError(null);
    onAdd({ process: proces, detail: null, sourceUrl: bron });
    setProcess('');
    setSourceUrl('');
  }

  return (
    <div style={{ marginTop: 12, borderTop: `1px solid ${theme.color.border}`, paddingTop: 12 }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 7 }}>Proces toevoegen</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={process}
          onChange={(e) => setProcess(e.target.value)}
          placeholder="bijv. hardverchromen"
          style={{ ...inputStyle, flex: '2 1 180px' }}
        />
        <input
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          placeholder="https://… (pagina waar dit staat)"
          style={{ ...inputStyle, flex: '3 1 240px' }}
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          style={{ background: 'transparent', border: `1px solid ${theme.color.borderStrong}`, borderRadius: 7, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}
        >
          Toevoegen
        </button>
      </div>
      <div style={{ marginTop: 6, fontSize: 11.5, color: theme.color.subtle, lineHeight: 1.5 }}>
        De bron-URL is verplicht: elke procesregel moet terug te vinden zijn, ook een die je zelf
        invult.
      </div>
      {error && <div style={{ marginTop: 6, fontSize: 12.5, color: '#A82A2A' }}>{error}</div>}
    </div>
  );
}
